import { Children, Fragment, cloneElement, isValidElement, useEffect, useRef, useState, type ReactNode } from 'react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { Link } from 'react-router-dom'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from 'recharts'
import { Clock, CheckCircle, DollarSign, Smile, BarChart3, TrendingUp, Download, Users, ChevronDown, TableProperties, Activity, Target, Presentation, AlertTriangle, MessageSquare, ShieldAlert } from 'lucide-react'
import MultiSelect from '../components/MultiSelect'
import Select from '../components/Select'
import Popover from '../components/Popover'
import { ChartCard, Dot, Badge } from '../components/ChartCard'
import InfoTooltip from '../components/InfoTooltip'
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../components/ui/chart'
import LoadingSpinner from '../components/LoadingSpinner'
import ReportsTabs from '../components/ReportsTabs'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import {
  calculateRealisedKPIs,
  calculateKPIsByTier,
  calculateProjectedKPIsByTier,
  calculateRealisedKPIsByPortfolio,
  calculateProjectedKPIsByPortfolio,
  calculateAverageSatisfactionScore,
  formatCurrency,
  formatNumber,
  groupResponsesByMonth,
  projectAdoptionPct,
  getLateProjects,
  hrsForPortfolio,
  computeProjectedOTIFAC,
} from '../lib/utils'
import { yearTotal as ledgerYearTotal, yearToDateTotal as ledgerYTD, yearRemainingTotal as ledgerYearRemaining } from '../lib/ledger'
import { valueByYear, withinWindow, type YearValue } from '../lib/valueByYear'
import { CHART_COLORS, GRID_STROKE, AXIS_TICK } from '../lib/chartOpts'
import { exportProjectsCsv, exportAllDataCsv, exportRiskRegisterCsv } from '../lib/exportCsv'
import { riskScore, riskRag, effectiveRiskScore, ragCounts, riskForecastBandLabel, DEFAULT_RISK_FORECAST_MIN_SCORE, RAG_BAR, RAG_PILL, RAG_LABEL } from '../lib/risk'
import { TIERS } from '../lib/constants'
import type { Project, SurveyResponse, Settings } from '../types'

const KPI_CARDS = [
  { key: 'onTime',       title: 'On Time',      icon: Clock },
  { key: 'inFull',       title: 'In Full',      icon: CheckCircle },
  { key: 'atCost',       title: 'At Cost',      icon: DollarSign },
  { key: 'satisfaction', title: 'Satisfaction', icon: Smile },
] as const

// KPI health colours (RAG). A strong colour should mean "watch-out", so the
// tiles go green/amber/red on the value itself - the eye lands on whatever is
// underperforming. Thresholds agreed with the team; simple to move to Settings.
const RAG = { green: '#00843D', amber: '#F88806', red: '#EF3340' } as const
function ragColor(key: string, value: number): string {
  if (key === 'satisfaction') return value >= 4 ? RAG.green : value >= 3 ? RAG.amber : RAG.red
  if (key === 'adoption') return value >= 80 ? RAG.green : value >= 60 ? RAG.amber : RAG.red
  return value >= 90 ? RAG.green : value >= 75 ? RAG.amber : RAG.red // On Time / In Full / At Cost (%)
}

// TOGGLES defines which report sections are available and whether they're on by default.
// Accent hexes mapped to Danone brand palette.
const TOGGLES = [
  { key: 'tier',      label: 'KPIs by Tier',         cls: 'bg-[#005EB8]/5 border-[#005EB8]/20 text-[#005EB8]',     accent: '#005EB8', defaultOn: true  },
  { key: 'trend',     label: 'Satisfaction Trend',   cls: 'bg-[#00ACED]/10 border-[#00ACED]/25 text-[#0077A8]',    accent: '#00ACED', defaultOn: true  },
  { key: 'value',     label: 'Value Delivered',      cls: 'bg-[#002677]/5 border-[#002677]/20 text-[#002677]',     accent: '#002677', defaultOn: true  },
  { key: 'summary',   label: 'Summary Table',        cls: 'bg-slate-50 border-slate-200 text-slate-800',           accent: '#475569', defaultOn: true  },
  { key: 'late',      label: 'Late Projects',         cls: 'bg-[#EF3340]/10 border-[#EF3340]/25 text-[#B8242E]',    accent: '#EF3340', defaultOn: true  },
  { key: 'risks',     label: 'Open Risks',            cls: 'bg-[#F88806]/10 border-[#F88806]/25 text-[#B8640B]',    accent: '#F88806', defaultOn: true  },
  { key: 'adoption',  label: 'Adoption Rate',        cls: 'bg-[#F88806]/10 border-[#F88806]/25 text-[#B8640B]',    accent: '#F88806', defaultOn: false },
  { key: 'cost',      label: 'Cost Summary',         cls: 'bg-[#EF3340]/10 border-[#EF3340]/25 text-[#B8242E]',    accent: '#EF3340', defaultOn: false },
  { key: 'rgm',       label: 'RGM',                  cls: 'bg-[#00843D]/10 border-[#00843D]/25 text-[#00843D]',    accent: '#00843D', defaultOn: false },
] as const

// The hero's hours as one bar cut by calendar year: each year's slice is as
// wide as its share of the hours, solid for actual and translucent for
// forecast, the same language as the goal bar above it. A slice keeps a
// minimum width so a small year still has room for its figures.
function YearBars({ rows, compact = false }: { rows: YearValue[]; compact?: boolean }) {
  if (rows.length === 0) return null
  const share = (part: number, whole: number) => (whole > 0 ? `${(part / whole) * 100}%` : '0%')
  const split = (r: YearValue) => [
    r.realised > 0 ? `${formatNumber(r.realised)} actual` : null,
    r.forecast > 0 ? `${formatNumber(r.forecast)} forecast` : null,
  ].filter(Boolean).join(' · ')
  return (
    <div className={`flex border-t border-white/15 ${compact ? 'mt-2 pt-2 gap-1.5' : 'mt-4 pt-4 gap-2'}`}>
      {rows.map(r => {
        const total = r.realised + r.forecast
        return (
          <div key={r.year} className="min-w-0" style={{ flex: `${total} 1 0%`, minWidth: compact ? 64 : 88 }}>
            <div className={`flex items-baseline gap-1.5 ${compact ? 'mb-1' : 'mb-1.5'}`}>
              <span className={`font-bold ${compact ? 'text-[10px]' : 'text-xs'}`}>{r.year}</span>
              <span className={`font-semibold tabular-nums ${compact ? 'text-[10px]' : 'text-sm'}`}>
                {formatNumber(total)}<span className="font-normal opacity-80"> hrs</span>
              </span>
            </div>
            <div className={`flex overflow-hidden rounded-full bg-white/20 ${compact ? 'h-1.5' : 'h-2'}`}>
              <div className="h-full bg-white" style={{ width: share(r.realised, total) }} />
              <div className="h-full" style={{ width: share(r.forecast, total), background: 'rgba(255,255,255,0.45)' }} />
            </div>
            <div className={`truncate opacity-90 tabular-nums ${compact ? 'mt-0.5 text-[9px]' : 'mt-1 text-[11px]'}`}>{split(r)}</div>
          </div>
        )
      })}
    </div>
  )
}

export default function Reports() {
  useDocumentTitle('Delivery Performance')
  const api = useApi()
  const { data, loading } = useData(() =>
    Promise.all([
      api.getProjects(),
      api.getSurveyResponses(),
      api.getSettings(),
    ]).then(([projects, responses, settings]) => ({ projects, responses, settings }))
  )
  const [portfolioView, setPortfolioView] = useState(false)
  const [portfolioFilter, setPortfolioFilter] = useState<string[]>([])
  const [tierFilter, setTierFilter] = useState<string[]>([])
  const [teamFilter, setTeamFilter] = useState<string[]>([])
  const [yearFilter, setYearFilter] = useState<string>('')
  const [hrsMode, setHrsMode] = useState<'owner' | 'recipient'>('owner')
  // Actuals = delivered projects only. Forecast = projected landing position
  // including in-flight projects (see computeProjectedOTIFAC) - the default.
  const [kpiMode, setKpiMode] = useState<'actuals' | 'forecast'>('forecast')
  const [visible, setVisible] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(TOGGLES.map(t => [t.key, t.defaultOn]))
  )
  const [exportOpen, setExportOpen] = useState(false)
  const [presentMode, setPresentMode] = useState<'portfolio' | 'wordcloud' | false>(false)
  const [presentOpen, setPresentOpen] = useState(false)
  const presentRef = useRef<HTMLDivElement>(null)
  const exportRef = useRef<HTMLDivElement>(null)

  if (loading || !data) return <LoadingSpinner />

  const { projects, responses, settings } = data as {
    projects: Project[]
    responses: SurveyResponse[]
    settings: Settings
  }
  const { subPortfolios, onTimeDays, atCostPct } = settings
  const riskMinScore = settings.riskForecastMinScore ?? DEFAULT_RISK_FORECAST_MIN_SCORE
  const riskBand = riskForecastBandLabel(riskMinScore)
  const portfolioGroups = settings.portfolios || []
  // In portfolio view, the filter options are portfolio names; a project matches
  // if its subPortfolio is inside any selected portfolio's sub-port list.
  const portfolioFilterOptions = portfolioView
    ? portfolioGroups.map(g => g.name)
    : subPortfolios
  const subPortsForFilter = (selected: string[]): string[] => {
    if (!portfolioView) return selected
    return portfolioGroups
      .filter(g => selected.includes(g.name))
      .flatMap(g => g.subPortfolios)
  }
  const hasUnassigned = projects.some(p => !p.team)
  const teams = [...(settings.teams || []), ...(hasUnassigned ? ['Unassigned'] : [])]
  const targetHrs = settings.valueRealisedTargetHrs || 0
  const targetStartYear = settings.valueRealisedTargetStartYear
  const targetEndYear = settings.valueRealisedTargetEndYear
  const targetRangeLabel = targetStartYear && targetEndYear
    ? targetStartYear === targetEndYear ? `${targetEndYear}` : `${targetStartYear}-${targetEndYear}`
    : targetEndYear ? `by ${targetEndYear}` : null
  // How the tile's info text names the years its hours are limited to.
  const windowPhrase = targetStartYear && targetEndYear ? `in ${targetRangeLabel}`
    : targetEndYear ? `by ${targetEndYear}` : targetStartYear ? `from ${targetStartYear}` : null

  // Years available across both delivered and forecast projects.
  // Forecast projects with an H2 due date (Jul-Dec) are attributed to the
  // following year - matching Scorecard logic - because savings accrue after
  // go-live and a Dec go-live produces essentially zero hours in that year.
  const yearOf = (p: Project) => {
    if (p.status === 'complete') {
      return p.actualGoLive ? new Date(p.actualGoLive).getFullYear().toString() : null
    }
    if (!p.dueDate) return null
    const d = new Date(p.dueDate)
    const y = d.getFullYear()
    const m = d.getMonth() + 1 // 1-12
    return (m >= 7 ? y + 1 : y).toString()
  }
  const availableYears = [...new Set(projects.map(yearOf).filter(Boolean) as string[])].sort().reverse()

  const passesCommonFilters = (p: Project) => {
    const subPortMatch = portfolioFilter.length === 0 || subPortsForFilter(portfolioFilter).includes(p.subPortfolio) || (!portfolioView && portfolioFilter.includes(p.subPortfolio))
    return subPortMatch &&
      (!tierFilter.length || tierFilter.includes(p.tier)) &&
      (!teamFilter.length || teamFilter.includes(p.team || 'Unassigned'))
  }

  const delivered = projects.filter(p =>
    p.status === 'complete' &&
    passesCommonFilters(p) &&
    (!yearFilter || (p.actualGoLive && new Date(p.actualGoLive).getFullYear().toString() === yearFilter))
  )
  const forecast = projects.filter(p =>
    p.status === 'forecast' &&
    passesCommonFilters(p) &&
    (!yearFilter || (p.dueDate && new Date(p.dueDate).getFullYear().toString() === yearFilter))
  )
  const filtered = delivered // legacy alias used in chart datasets / sections below
  const active = delivered
  const deliveredIds = new Set(delivered.map(p => p.id))
  const filteredResponses = responses.filter(r => deliveredIds.has(r.projectId))

  // Tokenise additionalComments into a frequency map for the word cloud slide.
  const wordCloudData = (() => {
    const freq: Record<string, number> = {}
    const stopWords = new Set([
      'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of',
      'with', 'by', 'from', 'is', 'was', 'are', 'were', 'be', 'been', 'being',
      'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
      'may', 'might', 'this', 'that', 'it', 'its', 'very', 'not', 'no', 'we', 'i',
      'they', 'our', 'their', 'your', 'my', 'us', 'so', 'if', 'as', 'up', 'out',
      'all', 'more', 'also', 'been', 'into', 'just', 'than', 'then', 'which',
    ])
    for (const r of filteredResponses) {
      if (!r.additionalComments) continue
      const words = r.additionalComments.toLowerCase().match(/[a-z]+/g) || []
      for (const w of words) {
        if (w.length < 3 || stopWords.has(w)) continue
        freq[w] = (freq[w] || 0) + 1
      }
    }
    return Object.entries(freq).map(([text, value]) => ({ text, value }))
  })()

  const kpis = calculateRealisedKPIs(delivered, filteredResponses, onTimeDays, atCostPct)
  const projected = computeProjectedOTIFAC(delivered, forecast, onTimeDays, atCostPct, riskMinScore)
  // KPIs by Tier follows the Actuals/Forecast toggle, mirroring the headline.
  const kpisByTier = kpiMode === 'forecast'
    ? calculateProjectedKPIsByTier(delivered, forecast, onTimeDays, atCostPct, riskMinScore)
    : calculateKPIsByTier(delivered, onTimeDays, atCostPct)
  // In portfolio view, aggregate each portfolio's sub-ports into one row.
  // Portfolio Summary OTIFAC columns follow the Actuals/Forecast toggle too.
  // Satisfaction (portSat) and hours saved stay realised - there's no forecast
  // for those. In Forecast mode `count` = completed + in-flight, so pipeline-
  // only portfolios show up.
  const kpisByPortfolio = portfolioView
    ? portfolioGroups.map(g => {
        const inPort = delivered.filter(p => g.subPortfolios.includes(p.subPortfolio))
        const inPortForecast = forecast.filter(p => g.subPortfolios.includes(p.subPortfolio))
        const now2 = new Date()
        const realisedK = calculateRealisedKPIs(inPort, responses.filter(r => inPort.some(p => p.id === r.projectId)), onTimeDays, atCostPct)
        const otif = kpiMode === 'forecast'
          ? computeProjectedOTIFAC(inPort, inPortForecast, onTimeDays, atCostPct, riskMinScore)
          : { onTime: realisedK.onTime, inFull: realisedK.inFull, atCost: realisedK.atCost }
        const count = kpiMode === 'forecast' ? inPort.length + inPortForecast.length : inPort.length
        const curYr = now2.getFullYear(), curMo = now2.getMonth() + 1
        const hrsSaved = hrsMode === 'owner'
          ? inPort.reduce((s, p) => {
              const ledger = p.monthlyLedger
              if (!ledger) return s
              const yrs = new Set<number>()
              for (const key of Object.keys(ledger)) { const y = Number(key.slice(0, 4)); if (!Number.isNaN(y)) yrs.add(y) }
              let total = 0
              for (const y of yrs) {
                if (y < curYr) total += ledgerYearTotal(p.monthlyLedger, y)
                else if (y === curYr) total += ledgerYTD(p.monthlyLedger, y, curMo)
              }
              return s + total
            }, 0)
          : delivered.reduce((s, p) =>
              s + g.subPortfolios.reduce((ps, sp) => ps + hrsForPortfolio(p, sp, curYr, curMo), 0)
            , 0)
        return { portfolio: g.name, count, onTime: otif.onTime, inFull: otif.inFull, atCost: otif.atCost, adoption: realisedK.adoption, techSat: realisedK.techSat, peopleSat: realisedK.peopleSat, satisfaction: realisedK.satisfaction, hrsSaved }
      })
    : kpiMode === 'forecast'
      ? calculateProjectedKPIsByPortfolio(delivered, forecast, subPortfolios, onTimeDays, atCostPct, hrsMode, riskMinScore)
      : calculateRealisedKPIsByPortfolio(delivered, subPortfolios, onTimeDays, atCostPct, hrsMode)
  // Sort portfolios by hrs saved descending so the biggest contributors read
  // top-down. Empty portfolios (count=0) are filtered out entirely.
  const activePortfolios = kpisByPortfolio.filter(p => p.count > 0).sort((a, b) => b.hrsSaved - a.hrsSaved)
  // Late projects: forecast projects past their dueDate. Intentionally ignores
  // the year filter - a project overdue from last year is still critically late.
  const lateProjects = getLateProjects(projects).filter(passesCommonFilters)
  // Open risks across every non-cancelled project passing the filters. Like
  // Late Projects, this ignores the year filter - an open risk is current
  // regardless of which year its project reports under.
  const riskProjects = projects.filter(p => p.status !== 'cancelled' && passesCommonFilters(p))
  const openRisks = riskProjects
    .flatMap(p => (p.risks ?? []).filter(r => r.status === 'open').map(risk => ({ project: p, risk })))
    .sort((a, b) => effectiveRiskScore(b.risk) - effectiveRiskScore(a.risk))
  // Danone RAG rollup - "how many reds / ambers / greens" across open risks.
  const openRagCounts = ragCounts(openRisks.map(({ risk }) => risk))

  // Response rate for the filtered set: responses ÷ surveys-actually-sent.
  // Shown under the Satisfaction tile so "5/5 sat" is always read with the
  // sample size it came from.
  const surveysSent = delivered.filter(p => !!p.surveySentAt).length
  const projectsWithResponses = delivered.filter(p => responses.some(r => r.projectId === p.id)).length
  const responseRatePct = surveysSent > 0 ? Math.round((projectsWithResponses / surveysSent) * 100) : 0

  // Headline numbers - Y forecast / Z realised / target T
  //
  // Realised hours source: project monthly ledgers. Completed projects seed
  // their own ledger on forecast→complete, and owners can adjust. Filters
  // (team, portfolio, tier) now apply because the ledger lives on the project.
  const now = new Date()
  const curYear = now.getFullYear()
  const curMonth = now.getMonth() + 1

  // The hours saved, by the year they land in. Realised = ledger months up to
  // this one; forecast = later ledger months plus the pipeline's expected
  // value. Only the years inside the goal window count, so the headline, the
  // progress against the goal and the year rows are the same hours.
  const yearValues = withinWindow(valueByYear(delivered, forecast, curYear, curMonth), targetStartYear, targetEndYear)
  const realisedHrs = yearValues.reduce((s, r) => s + r.realised, 0)
  const forecastHrs = yearValues.reduce((s, r) => s + r.forecast, 0)
  const headlineHrs = realisedHrs + forecastHrs
  const targetPct = targetHrs > 0 ? Math.min(100, Math.round((realisedHrs / targetHrs) * 100)) : 0
  const forecastPct = targetHrs > 0 ? Math.min(100 - targetPct, Math.round((forecastHrs / targetHrs) * 100)) : 0

  // ── Chart datasets ───────────────────────────────────────────────────────────
  // Recharts wants a flat array of rows, one per x-axis category, with
  // one key per series. Chart config maps series keys -> label + colour
  // so tooltips and legends inherit the shadcn theming.

  // KPIs by Tier - one row per KPI (On Time / In Full / At Cost), one
  // numeric key per tier. Tier keys are stable across renders.
  const TIER_KEYS = ['blackLine', 'winning', 'stretch', 'licenceToOperate'] as const
  // Danone-brand tier palette, keyed by tier so colours stay consistent even
  // when some tiers are hidden for having no projects.
  const TIER_PALETTE_MAP: Record<string, string> = {
    'Black Line':        '#002677',
    'Winning':           CHART_COLORS.blue,
    'Stretch':           CHART_COLORS.amber,
    'License to Operate': CHART_COLORS.cyan,
  }
  const tierKeyFor = (tier: string) => TIER_KEYS[TIERS.indexOf(tier as typeof TIERS[number])] ?? 'licenceToOperate'
  // Only include tiers that actually have projects so Recharts doesn't reserve
  // empty slots in the grouped bars.
  const activeTiers = kpisByTier.filter(t => t.count > 0)
  const activeTierKeys = activeTiers.map(t => tierKeyFor(t.tier))
  const tierData = [
    { kpi: 'On Time', ...Object.fromEntries(activeTiers.map(t => [tierKeyFor(t.tier), t.onTime])) },
    { kpi: 'In Full', ...Object.fromEntries(activeTiers.map(t => [tierKeyFor(t.tier), t.inFull])) },
    { kpi: 'At Cost', ...Object.fromEntries(activeTiers.map(t => [tierKeyFor(t.tier), t.atCost])) },
  ]
  const tierConfig: ChartConfig = Object.fromEntries(
    activeTiers.map(t => [tierKeyFor(t.tier), { label: t.tier, color: TIER_PALETTE_MAP[t.tier] ?? CHART_COLORS.cyan }])
  )

  // Satisfaction trend - monthly averages, split into Overall (combined),
  // Solution (tech) and People (team) so a dip in either component is visible.
  const byMonth = groupResponsesByMonth(filteredResponses)
  const months = [...byMonth.keys()].sort()
  const monthLabels = months.map(m => new Date(m + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }))
  const avg1 = (vals: number[]) => vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10) / 10 : 0
  const monthAvgs = months.map(m => avg1(byMonth.get(m)!))   // combined - drives the badge
  const solByMonth = new Map<string, number[]>()
  const pplByMonth = new Map<string, number[]>()
  filteredResponses.forEach(r => {
    const k = r.submittedAt.substring(0, 7)
    if (!solByMonth.has(k)) { solByMonth.set(k, []); pplByMonth.set(k, []) }
    solByMonth.get(k)!.push(r.techSatisfaction)
    pplByMonth.get(k)!.push(r.peopleSatisfaction)
  })
  const trendData = monthLabels.length > 0
    ? months.map((m, i) => ({
        month: monthLabels[i],
        overall: monthAvgs[i],
        solution: avg1(solByMonth.get(m) ?? []),
        people: avg1(pplByMonth.get(m) ?? []),
      }))
    : [{ month: 'No data', overall: 0, solution: 0, people: 0 }]
  const trendConfig: ChartConfig = {
    overall:  { label: 'Overall',  color: '#002677' },
    solution: { label: 'Solution', color: '#005EB8' },
    people:   { label: 'People',   color: '#00ACED' },
  }

  // Badge rule: a high absolute score always reads positive - 5.0 → 4.8 is not
  // "Declining", it's still healthy. Direction (vs the previous month, with a
  // ±0.2 dead-band so rounding noise doesn't flip the label) only kicks in
  // below the 4.0 bar.
  const latestAvg = monthAvgs[monthAvgs.length - 1]
  const prevAvg = monthAvgs[monthAvgs.length - 2]
  const satisfactionTrend =
    monthAvgs.length === 0 ? 'Stable'
    : latestAvg >= 4.0 ? 'Healthy'
    : monthAvgs.length < 2 ? 'Stable'
    : latestAvg - prevAvg >= 0.2 ? 'Improving'
    : latestAvg - prevAvg <= -0.2 ? 'Declining'
    : 'Stable'

  // Value Delivered - two series per sub-portfolio.
  //  · Realised = past ledger hours on completed projects (prior years + YTD)
  //  · Forecast = ledger hours dated after this month on completed projects
  //               + expectedValue on forecast-status (pipeline) projects
  // One completed project with a multi-year ledger therefore contributes to
  // both bars: banked months → Realised, future months → Forecast.
  const futureLedgerHrsFor = (project: Project): number => {
    const ledger = project.monthlyLedger
    if (!ledger) return 0
    const years = new Set<number>()
    for (const key of Object.keys(ledger)) {
      const y = Number(key.slice(0, 4))
      if (!Number.isNaN(y)) years.add(y)
    }
    let total = 0
    for (const y of years) {
      if (y === curYear) total += ledgerYearRemaining(ledger, y, curMonth)
      else if (y > curYear) total += ledgerYearTotal(ledger, y)
    }
    return total
  }
  // Build Value Delivered data. In portfolio view, group by portfolio name
  // (each portfolio aggregates its sub-ports). In sub-portfolio view, use
  // the sub-portfolio list as before.
  const valueGroupings: { label: string; subPorts: string[] }[] = portfolioView
    ? portfolioGroups.map(g => ({ label: g.name, subPorts: g.subPortfolios }))
    : subPortfolios.map(s => ({ label: s, subPorts: [s] }))

  const valueDataAll = valueGroupings
    .map(({ label, subPorts }) => {
      const portfolio = label
      let realised: number
      let futureOnCompleted: number
      let pipeline: number
      if (hrsMode === 'owner') {
        const completedInPort = filtered.filter(pr => subPorts.includes(pr.subPortfolio))
        const forecastInPort = forecast.filter(pr => subPorts.includes(pr.subPortfolio))
        realised = completedInPort.reduce((s, pr) => {
          const ledger = pr.monthlyLedger
          if (!ledger) return s
          const yrs = new Set<number>()
          for (const key of Object.keys(ledger)) { const y = Number(key.slice(0, 4)); if (!Number.isNaN(y)) yrs.add(y) }
          let total = 0
          for (const y of yrs) {
            if (y < curYear) total += ledgerYearTotal(ledger, y)
            else if (y === curYear) total += ledgerYTD(ledger, y, curMonth)
          }
          return s + total
        }, 0)
        futureOnCompleted = completedInPort.reduce((s, pr) => s + futureLedgerHrsFor(pr), 0)
        pipeline = forecastInPort.reduce((s, pr) => s + (pr.expectedValue || 0), 0)
      } else {
        realised = filtered.reduce((s, pr) =>
          s + subPorts.reduce((ps, sp) => ps + hrsForPortfolio(pr, sp, curYear, curMonth), 0)
        , 0)
        futureOnCompleted = filtered.reduce((s, pr) => {
          const full = futureLedgerHrsFor(pr)
          if (!pr.valueSubPortfolios) return s + (subPorts.includes(pr.subPortfolio) ? full : 0)
          const pct = subPorts.reduce((ps, sp) => ps + (pr.valueSubPortfolios![sp] ?? 0), 0)
          return s + full * (pct / 100)
        }, 0)
        pipeline = forecast.reduce((s, pr) => {
          if (!pr.valueSubPortfolios) return s + (subPorts.includes(pr.subPortfolio) ? (pr.expectedValue || 0) : 0)
          const pct = subPorts.reduce((ps, sp) => ps + (pr.valueSubPortfolios![sp] ?? 0), 0)
          return s + (pr.expectedValue || 0) * (pct / 100)
        }, 0)
      }
      return {
        portfolio,
        realised,
        forecast: futureOnCompleted + pipeline,
      }
    })
    // Drop zero-value portfolios so the chart isn't padded with empty slots,
    // then sort left→right by total hours (realised + forecast) descending.
    .filter(d => d.realised + d.forecast > 0)
    .sort((a, b) => (b.realised + b.forecast) - (a.realised + a.forecast))
  // Cap the chart at the top 10 portfolios by total hours so the bars stay
  // readable (sub-portfolio view has 27). The % realised badge and totals
  // below are still computed over every portfolio via `valueDataAll`.
  const valueTruncated = valueDataAll.length > 10
  const valueData = valueDataAll.slice(0, 10)
  const valueConfig: ChartConfig = {
    realised: { label: 'Realised', color: '#005EB8' },
    forecast: { label: 'Forecast', color: '#00ACED' },
  }

  // Realised % = share of total expected value already banked.
  // Denominator is realised + forecast, which is exactly the bar stack total
  // - so the badge and the chart always tell the same story.
  const totalRealised = valueDataAll.reduce((s, d) => s + d.realised, 0)
  const totalForecastHrs = valueDataAll.reduce((s, d) => s + d.forecast, 0)
  const realisedPct = (totalRealised + totalForecastHrs) > 0
    ? Math.round((totalRealised / (totalRealised + totalForecastHrs)) * 100)
    : 0
  const totalForecast = active.reduce((s, p) => s + p.forecastCost, 0)
  const totalActual = active.reduce((s, p) => s + (p.actualCost || 0), 0)
  const costVar = totalForecast > 0 ? Math.round(((totalForecast - totalActual) / totalForecast) * 100) : 0

  // Per-project adoption rows: any project whose adoptionMode contributes
  // a number. 'na'-mode projects and missing-data projects drop out.
  const adoptionRows = active
    .map(p => ({ project: p, pct: projectAdoptionPct(p) }))
    .filter((r): r is { project: Project; pct: number } => r.pct !== null)
  const shortName = (n: string) => n.length > 15 ? n.substring(0, 15) + '…' : n

  const adoptionData = adoptionRows.map(r => ({ project: shortName(r.project.name), adoption: r.pct }))
  const adoptionConfig: ChartConfig = { adoption: { label: 'Adoption %', color: CHART_COLORS.amber } }


  // ── Portfolio summary helpers ────────────────────────────────────────────────

  // Colour thresholds: ≥85% green, ≥70% amber, <70% red
  const pillCls = (v: number) =>
    v >= 85 ? 'bg-[#00843D]/10 text-[#00843D]' :
    v >= 70 ? 'bg-[#F88806]/10 text-[#F88806]' :
              'bg-[#EF3340]/10 text-[#EF3340]'

  const portSat = (portfolio: string) => {
    const ids = filtered.filter(p => p.subPortfolio === portfolio).map(p => p.id)
    const r = responses.filter(r => ids.includes(r.projectId))
    return calculateAverageSatisfactionScore(r)
  }

  // Tooltip copy for the KPI tiles and charts. Kept inline so the onTimeDays /
  // atCostPct tolerance values stay in sync with Settings. All tiles are
  // computed only over `status === 'complete'` projects (see calculateRealisedKPIs).
  const kpiInfo = {
    onTime: (
      <>
        <strong>On Time</strong>
        <p>% of <strong>completed</strong> projects where actual go-live was within <strong>{onTimeDays} days</strong> of the due date.</p>
        <p className="text-slate-300">Only completed projects are counted. For overdue forecast projects, see the <strong>Late Projects</strong> section.</p>
      </>
    ),
    inFull: (
      <>
        <strong>In Full</strong>
        <p>% of completed projects where the delivery lead marked <strong>Delivered In Full = Yes</strong>.</p>
        <p className="text-slate-300">Set by delivery lead on the project page.</p>
      </>
    ),
    atCost: (
      <>
        <strong>At Cost</strong>
        <p>% of completed projects where actual cost was within <strong>{atCostPct}%</strong> of forecast cost.</p>
        <p className="text-slate-300">Projects without an actual cost are excluded (no penalty).</p>
      </>
    ),
    satisfaction: (
      <>
        <strong>Satisfaction</strong>
        <p>Average score (1-5) across all survey responses. Solution and Team sub-scores are averaged together per respondent.</p>
        <p className="text-slate-300">Responses are filtered to match the current project filters.</p>
      </>
    ),
  } as const

  // ── Slide view ──────────────────────────────────────────────────────────────
  // 16:9 PowerPoint frame (1280 × 720) scaled to fit viewport. Screenshot lands
  // at exact PPT aspect ratio for slide copy-paste. Contains headline Hours tile,
  // OTIFAC KPI tiles, plus the four headline reports packed into a single frame.

  if (presentMode === 'wordcloud') {
    const commentsWithText = filteredResponses.filter(r => r.additionalComments)
    const projectsWithComments = new Set(commentsWithText.map(r => r.projectId)).size
    const avgSat = kpis.satisfaction
    const BRAND_COLORS = ['#005EB8', '#00ACED', '#002677', '#54AD18', '#F88806', '#EF3340']
    return (
      <SlideFrame onExit={() => setPresentMode(false)} width={1280} height={720} extraControls={
        <div className="relative group">
          <button disabled className="px-3 py-1.5 rounded-lg bg-white/10 text-white/50 text-xs font-semibold border border-dashed border-white/20 cursor-not-allowed select-none">
            Redact names
          </button>
          <div className="absolute right-0 top-full mt-1.5 w-20 bg-slate-800 text-white text-[10px] text-center rounded-lg px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity">
            Coming soon
          </div>
        </div>
      }>
        <div className="w-full h-full flex flex-col bg-white">
          {/* Header */}
          <div className="flex items-center justify-between px-8 pt-6 pb-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#00ACED] mb-0.5">Stakeholder Feedback</p>
              <h1 className="text-2xl font-bold text-[#002677] leading-none">What people are saying</h1>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mx-8 mb-3 grid grid-cols-3 gap-3">
            {[
              { value: filteredResponses.length, label: 'Total responses' },
              { value: commentsWithText.length,  label: `Comment${commentsWithText.length !== 1 ? 's' : ''} · ${projectsWithComments} project${projectsWithComments !== 1 ? 's' : ''}` },
              { value: avgSat > 0 ? `${avgSat}/5` : '-', label: 'Avg satisfaction' },
            ].map(({ value, label }) => (
              <div key={label} className="bg-slate-50 rounded-xl px-4 py-2.5 flex items-center gap-3 border border-slate-100">
                <span className="text-2xl font-extrabold text-[#005EB8] leading-none tabular-nums">{value}</span>
                <span className="text-[10px] font-semibold text-slate-400 leading-tight">{label}</span>
              </div>
            ))}
          </div>

          {/* Word cloud */}
          <div className="flex-1 flex items-center justify-center overflow-hidden px-4 pb-4">
            {wordCloudData.length === 0 ? (
              <div className="text-slate-300 text-lg font-semibold">No feedback comments yet</div>
            ) : (
              <SpiralWordCloud words={wordCloudData} width={1200} height={490} colors={BRAND_COLORS} />
            )}
          </div>
        </div>
      </SlideFrame>
    )
  }

  if (presentMode === 'portfolio') {
    const SLIDE_W = 1280
    const SLIDE_H = 720
    return (
      <SlideFrame onExit={() => setPresentMode(false)} width={SLIDE_W} height={SLIDE_H}>
        <div className="w-full h-full flex flex-col gap-3 p-6 bg-white">
          {/* Slide header */}
          <div className="flex items-baseline justify-between">
            <div className="flex items-baseline gap-2">
              <h1 className="text-xl font-bold text-[#002677] leading-tight">Delivery Performance</h1>
              <span className="text-[10px] font-semibold text-slate-400">{filtered.length} completed · {forecast.length} pipeline · {filtered.length + forecast.length} total</span>
            </div>
            {targetRangeLabel && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Goal window {targetRangeLabel}</span>
            )}
          </div>

          {/* Top band: hero hours tile + 4 OTIFAC tiles */}
          <div className="grid grid-cols-5 gap-3">
            {/* Hero Hours Savings (spans 3) */}
            <div className="col-span-3 bg-[#005EB8] rounded-xl p-4 text-white flex flex-col justify-between">
              <div className="text-[10px] font-semibold uppercase opacity-80 tracking-wide">Projected Hour Savings</div>
              <div>
                <div className="font-danone text-4xl font-extrabold leading-none">{formatNumber(headlineHrs)}<span className="text-lg font-bold opacity-80 ml-1">hrs</span></div>
                <div className="text-[11px] opacity-90 mt-1">
                  {formatNumber(realisedHrs)} realised · {formatNumber(forecastHrs)} forecast
                  {targetHrs > 0 && <> · goal {formatNumber(targetHrs)}</>}
                </div>
              </div>
              {targetHrs > 0 && (
                <div>
                  <div className="flex items-center justify-between text-[10px] opacity-90 mb-1">
                    <span className="font-semibold">{targetPct}% realised{forecastPct > 0 ? ` · ${targetPct + forecastPct}% with forecast` : ''}</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden flex">
                    <div className="h-full bg-white rounded-l-full" style={{ width: `${targetPct}%` }} />
                    {forecastPct > 0 && (
                      <div className="h-full" style={{ width: `${forecastPct}%`, background: 'rgba(255,255,255,0.45)' }} />
                    )}
                  </div>
                </div>
              )}
              <YearBars rows={yearValues} compact />
            </div>
            {/* OTIFAC tiles (2×2, span 2) */}
            <div className="col-span-2 grid grid-cols-2 gap-3">
              {KPI_CARDS.map(card => {
                const Icon = card.icon
                const raw = kpis[card.key] as number
                const isSat = card.key === 'satisfaction'
                const isOT = card.key === 'onTime'
                const pct = isSat ? Math.round((raw / 5) * 100) : raw
                const ragC = ragColor(card.key, raw)
                return (
                  <div key={card.key} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: ragC }}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide leading-none">{card.title}</div>
                      <div className="text-xl font-bold text-slate-800 leading-tight mt-0.5">
                        {raw}{isSat ? <span className="text-[10px] font-normal text-slate-400 ml-0.5">/5</span> : <span className="text-xs font-semibold text-slate-400">%</span>}
                      </div>
                      {isSat && surveysSent > 0 && (
                        <div className="text-[9px] text-slate-400 leading-none mt-0.5">{responseRatePct}% response rate</div>
                      )}
                      {isOT && forecast.length > 0 && (
                        <div className={`text-[9px] leading-none mt-0.5 ${lateProjects.length > 0 ? 'text-[#EF3340] font-semibold' : 'text-slate-400'}`}>
                          {lateProjects.length > 0
                            ? `${lateProjects.length} of ${forecast.length} pipeline late`
                            : `${forecast.length} pipeline on track`}
                        </div>
                      )}
                      <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ragC }} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Four reports in a 2×2 grid. Top row is shorter (Satisfaction Trend
              doesn't need much height) so the bottom row - Value Delivered and
              Portfolio Summary - gets more room and all portfolios fit. */}
          <div className="grid grid-cols-2 gap-3 flex-1 min-h-0" style={{ gridTemplateRows: '2fr 3fr' }}>
            {/* KPIs by Tier */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-bold text-[#002677] uppercase tracking-wide">KPIs by Tier</h3>
                <span className="text-[9px] text-slate-400">{filtered.length} projects</span>
              </div>
              <ChartContainer config={tierConfig} className="aspect-auto flex-1 min-h-0 w-full">
                <BarChart data={tierData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
                  <XAxis dataKey="kpi" tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tickLine={false} axisLine={false} tick={{ fill: '#94a3b8', fontSize: 9 }} tickFormatter={v => `${v}%`} />
                  {activeTierKeys.map(key => (
                    <Bar key={key} dataKey={key} fill={`var(--color-${key})`} radius={[4, 4, 0, 0]} />
                  ))}
                </BarChart>
              </ChartContainer>
              <div className="flex flex-wrap items-center gap-2 mt-1 pt-1 border-t border-slate-100">
                {activeTiers.map(t => (
                  <span key={t.tier} className="flex items-center gap-1 text-[9px] text-slate-600">
                    <span className="w-2 h-2 rounded-sm" style={{ background: TIER_PALETTE_MAP[t.tier] ?? CHART_COLORS.cyan }} />
                    {t.tier}
                  </span>
                ))}
              </div>
            </div>

            {/* Satisfaction + Adoption side-by-side */}
            <div className="grid grid-cols-2 gap-3 min-h-0">
              {/* Satisfaction */}
              <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xs font-bold text-[#002677] uppercase tracking-wide">Satisfaction</h3>
                  <Smile className="w-4 h-4 text-[#00ACED]" />
                </div>
                <div className="flex-1 flex flex-col items-center justify-center gap-1">
                  <div className="text-4xl font-extrabold text-slate-800 leading-none">{kpis.satisfaction}<span className="text-base font-normal text-slate-400">/5</span></div>
                  <div className="w-full max-w-[120px] h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full rounded-full bg-[#00ACED]" style={{ width: `${Math.round((kpis.satisfaction / 5) * 100)}%` }} />
                  </div>
                  <div className="text-[9px] text-slate-400 mt-1 text-center leading-tight">
                    Sol {kpis.techSat.toFixed(1)} · Team {kpis.peopleSat.toFixed(1)}
                    {surveysSent > 0 && <><br />{responseRatePct}% response rate</>}
                  </div>
                </div>
              </div>
              {/* Adoption */}
              <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xs font-bold text-[#002677] uppercase tracking-wide">Adoption</h3>
                  <Users className="w-4 h-4" style={{ color: ragColor('adoption', kpis.adoption) }} />
                </div>
                <div className="flex-1 flex flex-col items-center justify-center gap-1">
                  <div className="text-4xl font-extrabold text-slate-800 leading-none">{kpis.adoption}<span className="text-base font-normal text-slate-400">%</span></div>
                  <div className="w-full max-w-[120px] h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(kpis.adoption, 100)}%`, background: ragColor('adoption', kpis.adoption) }} />
                  </div>
                  <div className="text-[9px] text-slate-400 mt-1 text-center leading-tight">
                    {adoptionRows.length} project{adoptionRows.length !== 1 ? 's' : ''} tracked
                  </div>
                </div>
              </div>
            </div>

            {/* Value Delivered */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-bold text-[#002677] uppercase tracking-wide">Value Delivered (hrs){valueTruncated ? ' · Top 10' : ''}</h3>
                <span className="text-[9px] font-semibold text-slate-500">{realisedPct}% realised</span>
              </div>
              <ChartContainer config={valueConfig} className="aspect-auto flex-1 min-h-0 w-full">
                <BarChart data={valueData} margin={{ top: 4, right: 12, left: -20, bottom: 4 }} barCategoryGap="12%">
                  <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
                  <XAxis dataKey="portfolio" tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 9 }} interval={0} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fill: '#94a3b8', fontSize: 9 }} tickFormatter={v => formatNumber(v)} domain={[0, 'auto']} allowDataOverflow={false} />
                  <Bar dataKey="realised" stackId="val" fill="var(--color-realised)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="forecast" stackId="val" fill="var(--color-forecast)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ChartContainer>
              <div className="flex flex-wrap items-center gap-2 mt-1 pt-1 border-t border-slate-100">
                <span className="flex items-center gap-1 text-[9px] text-slate-600"><span className="w-2 h-2 rounded-sm bg-[#005EB8]" />Realised</span>
                <span className="flex items-center gap-1 text-[9px] text-slate-600"><span className="w-2 h-2 rounded-sm bg-[#00ACED]" />Forecast</span>
              </div>
            </div>

            {/* Portfolio Summary */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-bold text-[#002677] uppercase tracking-wide">Portfolio Summary</h3>
                <span className="text-[9px] text-slate-400">{activePortfolios.length} sub-portfolios</span>
              </div>
              {activePortfolios.length === 0 ? (
                <p className="text-[10px] text-slate-400 text-center py-4">No data</p>
              ) : (
                <div className="rounded-md border border-slate-100 flex-1 min-h-0 overflow-hidden flex flex-col">
                  <table className="w-full text-[10px] h-full" style={{ tableLayout: 'auto' }}>
                    <thead>
                      <tr className="bg-slate-50 text-[9px] uppercase tracking-wide text-slate-500">
                        <th className="text-left font-semibold px-2 py-1">Portfolio</th>
                        <th className="text-center font-semibold px-1 py-1">OT</th>
                        <th className="text-center font-semibold px-1 py-1">IF</th>
                        <th className="text-center font-semibold px-1 py-1">AC</th>
                        <th className="text-center font-semibold px-1 py-1">Sat</th>
                        <th className="text-right font-semibold px-1 py-1">#</th>
                        <th className="text-right font-semibold px-2 py-1">Hrs</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {activePortfolios.map(p => {
                        const sat = portSat(p.portfolio)
                        const satPct = sat > 0 ? Math.round((sat / 5) * 100) : 0
                        const pill = (v: number | null, label: string) => v === null
                          ? <span className="text-slate-300">-</span>
                          : <span className={`inline-flex items-center justify-center min-w-[32px] px-1 py-0 rounded-full text-[9px] font-semibold ${pillCls(v)}`}>{label}</span>
                        return (
                          <tr key={p.portfolio}>
                            <td className="px-2 py-1 font-semibold text-slate-800 whitespace-nowrap">{p.portfolio}</td>
                            <td className="px-1 py-1 text-center">{pill(p.onTime, `${p.onTime}%`)}</td>
                            <td className="px-1 py-1 text-center">{pill(p.inFull, `${p.inFull}%`)}</td>
                            <td className="px-1 py-1 text-center">{pill(p.atCost, `${p.atCost}%`)}</td>
                            <td className="px-1 py-1 text-center">{sat > 0 ? pill(satPct, `${sat.toFixed(1)}`) : <span className="text-slate-300">-</span>}</td>
                            <td className="px-1 py-1 text-right tabular-nums text-slate-600">{p.count}</td>
                            <td className="px-2 py-1 text-right tabular-nums font-semibold text-slate-800">{formatNumber(p.hrsSaved)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </SlideFrame>
    )
  }

  return (
    <div>
      <ReportsTabs active="delivery" />
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Delivery Performance</h1>
          <p className="text-slate-500 text-sm">KPI performance across all projects</p>
        </div>
        <div className="flex items-center gap-2">
          <div ref={presentRef} className="relative">
            <button
              onClick={() => setPresentOpen(o => !o)}
              aria-haspopup="menu"
              aria-expanded={presentOpen}
              className="px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm border transition-colors bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              title="Open 16:9 slide view for screenshotting into a deck"
            >
              <Presentation className="w-4 h-4" /> Present <ChevronDown className={`w-4 h-4 transition-transform ${presentOpen ? 'rotate-180' : ''}`} />
            </button>
            <Popover open={presentOpen} anchorRef={presentRef} onClose={() => setPresentOpen(false)} width="content" align="end" className="w-52" label="Present">
              <div role="menu" className="py-1 min-h-0 overflow-y-auto">
                <button onClick={() => { setPresentMode('portfolio'); setPresentOpen(false) }} data-option role="menuitem" className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none w-full text-left">
                  <BarChart3 className="w-4 h-4 text-[#005EB8]" /> Portfolio View
                </button>
                <button onClick={() => { setPresentMode('wordcloud'); setPresentOpen(false) }} data-option role="menuitem" className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none w-full text-left">
                  <MessageSquare className="w-4 h-4 text-[#00ACED]" /> Feedback Word Cloud
                </button>
              </div>
            </Popover>
          </div>
          <div ref={exportRef} className="relative">
            <button onClick={() => setExportOpen(o => !o)} aria-haspopup="menu" aria-expanded={exportOpen}
                    className="bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 shadow-sm">
              <Download className="w-4 h-4" /> Export <ChevronDown className={`w-4 h-4 transition-transform ${exportOpen ? 'rotate-180' : ''}`} />
            </button>
            <Popover open={exportOpen} anchorRef={exportRef} onClose={() => setExportOpen(false)} width="content" align="end" className="w-56" label="Export">
              <div role="menu" className="py-1 min-h-0 overflow-y-auto">
                <button onClick={() => {
                  // Export every non-cancelled project that matches the
                  // portfolio/tier/team filters. Year filter is intentionally
                  // ignored - the export shows hours-saved across years, so
                  // restricting by go-live year would hide most of the data.
                  const exportable = projects.filter(p => p.status !== 'cancelled' && passesCommonFilters(p))
                  exportProjectsCsv(exportable)
                  setExportOpen(false)
                }} data-option role="menuitem" className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none w-full text-left">
                  <TableProperties className="w-4 h-4 text-[#00843D]" /> Hours Saved
                </button>
                <button onClick={() => {
                  exportAllDataCsv(projects.filter(p => p.status !== 'cancelled'))
                  setExportOpen(false)
                }} data-option role="menuitem" className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none w-full text-left">
                  <Download className="w-4 h-4 text-slate-500" /> All Data
                </button>
                <button onClick={() => {
                  // Danone-standard risk register: every risk (all statuses)
                  // on projects matching the current filters.
                  exportRiskRegisterCsv(riskProjects)
                  setExportOpen(false)
                }} data-option role="menuitem" className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none w-full text-left">
                  <ShieldAlert className="w-4 h-4 text-[#F88806]" /> Risk Register
                </button>
              </div>
            </Popover>
          </div>
        </div>
      </div>

      {/* ── Filters + toggles ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap gap-4 items-start">
        <div className="flex items-center gap-4 flex-wrap w-full">

          {/* ── View options ── */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">View</span>
            {portfolioGroups.length > 0 && (
              <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => { setPortfolioView(false); setPortfolioFilter([]) }}
                  className={`px-3 py-1.5 transition-colors ${!portfolioView ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                >
                  Sub-portfolio
                </button>
                <button
                  type="button"
                  onClick={() => { setPortfolioView(true); setPortfolioFilter([]) }}
                  className={`px-3 py-1.5 transition-colors ${portfolioView ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                >
                  Portfolio
                </button>
              </div>
            )}
            <div
              className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold"
              title="Controls how Hours Saved is attributed across portfolios. Does not affect On Time, In Full, At Cost or Satisfaction."
            >
              <button
                type="button"
                onClick={() => setHrsMode('owner')}
                className={`px-3 py-1.5 transition-colors ${hrsMode === 'owner' ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                Hrs: unlocked by
              </button>
              <button
                type="button"
                onClick={() => setHrsMode('recipient')}
                className={`px-3 py-1.5 transition-colors ${hrsMode === 'recipient' ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                Hrs: received by
              </button>
            </div>
            <div
              className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold"
              title={`Actuals scores completed projects only. Forecast projects the landing position: in-flight work counts as late once past due date + grace, and open Delivery / Scope / Cost risks rated ${riskBand} count as projected On Time / In Full / At Cost misses.`}
            >
              <button
                type="button"
                onClick={() => setKpiMode('actuals')}
                className={`px-3 py-1.5 transition-colors ${kpiMode === 'actuals' ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                Actuals
              </button>
              <button
                type="button"
                onClick={() => setKpiMode('forecast')}
                className={`px-3 py-1.5 transition-colors ${kpiMode === 'forecast' ? 'bg-[#00ACED] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                Forecast
              </button>
            </div>
          </div>

          {/* divider */}
          <div className="w-px self-stretch bg-slate-200" />

          {/* ── Data filters ── */}
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Filter</span>
            <MultiSelect
              values={portfolioFilter}
              onChange={setPortfolioFilter}
              options={portfolioFilterOptions}
              placeholder={portfolioView ? 'Portfolio' : 'Sub-portfolio'}
            />
            <MultiSelect values={tierFilter} onChange={setTierFilter} options={[...TIERS]} placeholder="Tier" />
            {teams.length > 0 && (
              <MultiSelect values={teamFilter} onChange={setTeamFilter} options={teams} placeholder="Team" />
            )}
            {availableYears.length > 0 && (
              <Select value={yearFilter} onChange={setYearFilter} options={availableYears} placeholder="All years" />
            )}
            {(portfolioFilter.length > 0 || tierFilter.length > 0 || teamFilter.length > 0 || yearFilter) && (
              <button onClick={() => { setPortfolioFilter([]); setTierFilter([]); setTeamFilter([]); setYearFilter('') }} className="text-sm text-danone-cyan font-semibold hover:text-danone-blue">Clear</button>
            )}
          </div>

        </div>
        <div className="border-t border-slate-100 w-full pt-3 mt-1">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Show sections:</div>
          <div className="flex flex-wrap gap-2">
            {TOGGLES.filter(t => t.key !== 'rgm' || portfolioFilter.length === 0 || portfolioFilter.includes('RGM')).map(t => (
              <label key={t.key} className={`flex items-center gap-2 rounded-lg px-3 py-1.5 cursor-pointer border ${t.cls} select-none`}>
                <input type="checkbox" checked={visible[t.key] || false} onChange={() => setVisible({ ...visible, [t.key]: !visible[t.key] })}
                  className="w-3.5 h-3.5 rounded" style={{ accentColor: '#005EB8' }} />
                <span className="text-xs font-medium">{t.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Headline value tile - the single most important number on the page.
          Flat Danone Blue per brand rules (no gradients). */}
      <div className="bg-[#005EB8] rounded-2xl shadow-md p-6 mb-4 text-white">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase opacity-80 tracking-wide flex items-center gap-1.5">
              <span>Projected Hour Savings</span>
              <InfoTooltip className="text-white/70 hover:text-white">
                <strong>Projected Hour Savings</strong>
                <p><strong>Realised</strong> - actual hours saved from completed projects (up to current month).</p>
                <p><strong>Forecast</strong> - remaining ledger months from completed projects + expected value from in-progress projects.</p>
                <p>Headline = Realised + Forecast. Goal is set in Settings.</p>
                {windowPhrase && <p>Only hours landing {windowPhrase}, the goal window, are counted.</p>}
              </InfoTooltip>
            </div>
            <div className="font-danone text-4xl font-extrabold leading-tight mt-1">{formatNumber(headlineHrs)} hrs</div>
            <div className="text-sm opacity-90 mt-1">
              {formatNumber(realisedHrs)} realised · {formatNumber(forecastHrs)} forecast
              {targetHrs > 0 && (
                <> · goal {formatNumber(targetHrs)}{targetRangeLabel ? ` by ${targetEndYear}` : ''}</>
              )}
            </div>
          </div>
          {targetHrs > 0 && (
            <div className="flex items-center gap-3">
              <Target className="w-6 h-6 opacity-80" />
              <div>
                <div className="text-xs font-semibold uppercase opacity-80">Progress vs Goal</div>
                <div className="text-2xl font-bold">{targetPct}%<span className="text-base font-semibold opacity-70 ml-1">realised</span></div>
                {forecastPct > 0 && (
                  <div className="text-sm font-semibold opacity-80">{targetPct + forecastPct}% with forecast</div>
                )}
                {targetRangeLabel && (
                  <div className="text-[11px] opacity-80">{targetRangeLabel}</div>
                )}
              </div>
            </div>
          )}
        </div>
        {targetHrs > 0 && (
          <div className="mt-4">
            <div className="h-3 bg-white/20 rounded-full overflow-hidden flex">
              <div className="h-full bg-white rounded-l-full" style={{ width: `${targetPct}%` }} />
              {forecastPct > 0 && (
                <div
                  className="h-full"
                  style={{
                    width: `${forecastPct}%`,
                    background: 'rgba(255,255,255,0.45)',
                    borderTopRightRadius: targetPct + forecastPct >= 100 ? '9999px' : 0,
                    borderBottomRightRadius: targetPct + forecastPct >= 100 ? '9999px' : 0,
                  }}
                />
              )}
            </div>
            <div className="flex items-center gap-4 mt-2 text-[11px] opacity-90">
              <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2 bg-white rounded-sm" /> Realised</span>
              <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2 rounded-sm bg-white/45" /> Forecast</span>
            </div>
          </div>
        )}
        <YearBars rows={yearValues} />
      </div>

      {/* ── KPI headline tiles ── */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        {KPI_CARDS.map(card => {
          const Icon = card.icon
          const isSat = card.key === 'satisfaction'
          // Satisfaction has no forecast signal - it always shows actuals.
          const isProjected = kpiMode === 'forecast' && !isSat
          const raw = isProjected ? projected[card.key as 'onTime' | 'inFull' | 'atCost'] : kpis[card.key] as number
          const pct = isSat ? Math.round((raw / 5) * 100) : raw
          const ragC = ragColor(card.key, raw)
          const projectedNote: Record<string, string> = {
            onTime: `In-flight projects count as late once today is past due date + grace, or they carry an open Delivery risk rated ${riskBand}.`,
            inFull: `In-flight projects with an open Scope risk rated ${riskBand} count as not-in-full.`,
            atCost: `In-flight projects with an open Cost risk rated ${riskBand} count as over-cost.`,
          }
          return (
            <div key={card.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: ragC }}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5 flex items-center gap-1.5">
                  <span>{card.title}</span>
                  {isProjected && <span className="px-1 py-0.5 rounded text-[9px] font-bold bg-[#00ACED]/15 text-[#0077A8]">PROJECTED</span>}
                  <InfoTooltip>
                    {kpiInfo[card.key]}
                    {isProjected && <p><strong>Forecast view:</strong> {projectedNote[card.key]} No signal = counts as a pass, so this is only as honest as the risk log.</p>}
                  </InfoTooltip>
                </div>
                <div className="text-2xl font-bold text-slate-800 leading-none">
                  {raw}{isSat ? <span className="text-sm font-normal text-slate-400 ml-0.5">/5</span> : <span className="text-base font-semibold text-slate-400">%</span>}
                </div>
                {isSat && (
                  <div className="text-xs text-slate-400 mt-1">
                    Sol. {kpis.techSat.toFixed(1)} · Team {kpis.peopleSat.toFixed(1)}
                    {surveysSent > 0 && <> · {responseRatePct}% response rate</>}
                  </div>
                )}
                {isProjected && (
                  <div className="text-xs text-slate-400 mt-1">
                    {delivered.length} completed + {forecast.length} in-flight
                  </div>
                )}
                <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ragC }} />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Charts: two masonry columns. A grid would size each row to its tallest
          card, leaving dead space under short cards (Late Projects, Cost Summary);
          here each card slots directly under the previous one in its column. ── */}
      <MasonryColumns>
        {visible.tier && (
          <ChartCard icon={BarChart3} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="KPIs by Tier" subtitle="On Time · In Full · At Cost"
            info={<>
              <strong>KPIs by Tier</strong>
              <p>The same On Time / In Full / At Cost percentages broken out by project tier (Black Line, Winning, Stretch, License to Operate).</p>
              <p>Follows the <strong>Actuals / Forecast</strong> toggle: Actuals counts completed projects only; Forecast blends in in-flight projects (a projected miss on an open Delivery/Scope/Cost risk).</p>
            </>}
            badge={kpiMode === 'forecast'
              ? <Badge cls="bg-[#00ACED]/15 text-[#0077A8]">Forecast</Badge>
              : <Badge cls="bg-slate-100 text-slate-600">{filtered.length} projects</Badge>}
            footer={<>{activeTiers.map(t => <Dot key={t.tier} color={({'Black Line':'bg-[#002677]','Winning':'bg-[#005EB8]','Stretch':'bg-[#F88806]','License to Operate':'bg-[#00ACED]'} as Record<string,string>)[t.tier] ?? 'bg-[#00ACED]'} label={t.tier} />)}</>}>
            <ChartContainer config={tierConfig} className="aspect-auto h-52 w-full">
              <BarChart data={tierData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
                <XAxis dataKey="kpi" tickLine={false} axisLine={false} tick={AXIS_TICK} />
                <YAxis domain={[0, 100]} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={v => `${v}%`} />
                <ChartTooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} content={<ChartTooltipContent formatter={(value, name, item) => (
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-[2px]" style={{ background: item.color }} />
                    <span className="text-muted-foreground">{tierConfig[name as string]?.label ?? name}</span>
                    <span className="ml-auto font-mono font-medium tabular-nums text-foreground">{value}%</span>
                  </div>
                )} />} />
                {activeTierKeys.map(key => (
                  <Bar key={key} dataKey={key} fill={`var(--color-${key})`} radius={[6, 6, 0, 0]} />
                ))}
              </BarChart>
            </ChartContainer>
          </ChartCard>
        )}
        {visible.trend && (
          <ChartCard icon={TrendingUp} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="Satisfaction Trend" subtitle="Overall · Solution · People, monthly avg (1-5)"
            info={<>
              <strong>Satisfaction Trend</strong>
              <p>Monthly average of all survey responses, split three ways so you can see which is moving: <strong>Overall</strong> (the combined score), <strong>Solution</strong> (how well the solution meets business needs) and <strong>People</strong> (project team professionalism &amp; communication).</p>
              <p>Badge: <strong>Healthy</strong> when the latest month's Overall averages 4.0+. Below that, it compares against the previous month (±0.2) for Improving / Declining / Stable.</p>
              <p className="text-slate-300">The Year filter intentionally does not apply here - we always show the full history so trends stay visible.</p>
            </>}
            badge={<Badge cls={satisfactionTrend === 'Healthy' || satisfactionTrend === 'Improving' ? 'bg-[#00843D]/10 text-[#00843D]' : satisfactionTrend === 'Declining' ? 'bg-[#EF3340]/10 text-[#EF3340]' : 'bg-slate-100 text-slate-600'}>{satisfactionTrend}</Badge>}
            footer={<div className="flex items-center justify-between w-full gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <Dot color="bg-[#002677]" label="Overall" />
                <Dot color="bg-[#005EB8]/50" label="Solution" />
                <Dot color="bg-[#00ACED]/50" label="People" />
              </div>
              <span className="text-xs font-semibold text-[#00ACED]">Avg {kpis.satisfaction}/5</span>
            </div>}>
            <ChartContainer config={trendConfig} className="aspect-auto h-52 w-full">
              <LineChart data={trendData} margin={{ top: 8, right: 16, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={AXIS_TICK} />
                <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tickLine={false} axisLine={false} tick={AXIS_TICK} />
                <ChartTooltip cursor={{ stroke: GRID_STROKE }} content={<ChartTooltipContent />} />
                <Line type="monotone" dataKey="solution" name="Solution" stroke="var(--color-solution)" strokeWidth={2} strokeOpacity={0.45}
                  dot={{ fill: 'var(--color-solution)', r: 2.5, strokeWidth: 1.5, stroke: '#fff', fillOpacity: 0.5 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="people" name="People" stroke="var(--color-people)" strokeWidth={2} strokeOpacity={0.45}
                  dot={{ fill: 'var(--color-people)', r: 2.5, strokeWidth: 1.5, stroke: '#fff', fillOpacity: 0.5 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="overall" name="Overall" stroke="var(--color-overall)" strokeWidth={2.5}
                  dot={{ fill: 'var(--color-overall)', r: 3.5, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ChartContainer>
          </ChartCard>
        )}
        {visible.value && (
          <ChartCard icon={Clock} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="Value Delivered" subtitle={valueTruncated ? 'Top 10 by hours · Realised · Forecast' : 'Realised · Forecast by portfolio'}
            info={<>
              <strong>Value Delivered (hours saved)</strong>
              <p><strong>Realised</strong> - past hours from completed project ledgers (prior years + YTD).</p>
              <p><strong>Forecast</strong> - ledger months dated after this month on completed projects, plus Expected Value on forecast-status (pipeline) projects.</p>
              <p className="text-slate-300">The <em>% realised</em> badge = realised ÷ (realised + forecast).</p>
            </>}
            badge={<Badge cls={realisedPct >= 80 ? 'bg-[#00843D]/10 text-[#00843D]' : realisedPct >= 60 ? 'bg-[#F88806]/10 text-[#F88806]' : 'bg-[#EF3340]/10 text-[#EF3340]'}>{realisedPct}% realised</Badge>}
            footer={<><Dot color="bg-[#005EB8]" label="Realised" /><Dot color="bg-[#00ACED]" label="Forecast" /></>}>
            <ChartContainer config={valueConfig} className="aspect-auto h-52 w-full">
              <BarChart data={valueData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
                <XAxis dataKey="portfolio" tickLine={false} axisLine={false} tick={AXIS_TICK} interval={0} />
                <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={v => formatNumber(v)} domain={[0, 'auto']} allowDataOverflow={false} />
                <ChartTooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} content={<ChartTooltipContent />} />
                <Bar dataKey="realised" stackId="val" fill="var(--color-realised)" radius={[0, 0, 0, 0]} />
                <Bar dataKey="forecast" stackId="val" fill="var(--color-forecast)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </ChartCard>
        )}
        {visible.summary && (
          <ChartCard icon={Activity} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="Portfolio Summary" subtitle={portfolioView ? 'KPI breakdown by portfolio' : 'KPI breakdown by sub-portfolio'}
            badge={kpiMode === 'forecast' ? <Badge cls="bg-[#00ACED]/15 text-[#0077A8]">Forecast</Badge> : undefined}
            info={<>
              <strong>Portfolio Summary</strong>
              <p>Same KPIs as the headline tiles (On Time / In Full / At Cost / Satisfaction), broken out per sub-portfolio.</p>
              <p>On Time / In Full / At Cost follow the <strong>Actuals / Forecast</strong> toggle. Satisfaction and Hrs always stay realised (no forecast for those).</p>
              <p className="text-slate-300">Pill colour: green ≥85%, amber ≥70%, red below. Dash = no data.</p>
            </>}>
            {activePortfolios.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No data</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-slate-100">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                      <th className="text-left font-semibold px-3 py-2">Portfolio</th>
                      <th className="text-center font-semibold px-2 py-2">On Time</th>
                      <th className="text-center font-semibold px-2 py-2">In Full</th>
                      <th className="text-center font-semibold px-2 py-2">At Cost</th>
                      <th className="text-center font-semibold px-2 py-2">Sat.</th>
                      <th className="text-right font-semibold px-2 py-2"># Proj</th>
                      <th className="text-right font-semibold px-3 py-2">Hrs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {activePortfolios.map(p => {
                      const sat = portSat(p.portfolio)
                      const satPct = sat > 0 ? Math.round((sat / 5) * 100) : 0
                      const pill = (v: number | null, label: string) => v === null
                        ? <span className="text-slate-300">-</span>
                        : <span className={`inline-flex items-center justify-center min-w-[44px] px-2 py-0.5 rounded-full text-[11px] font-semibold ${pillCls(v)}`}>{label}</span>
                      return (
                        <tr key={p.portfolio} className="hover:bg-slate-50/50">
                          <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{p.portfolio}</td>
                          <td className="px-2 py-2 text-center">{pill(p.onTime, `${p.onTime}%`)}</td>
                          <td className="px-2 py-2 text-center">{pill(p.inFull, `${p.inFull}%`)}</td>
                          <td className="px-2 py-2 text-center">{pill(p.atCost, `${p.atCost}%`)}</td>
                          <td className="px-2 py-2 text-center">{sat > 0 ? pill(satPct, `${sat.toFixed(1)}/5`) : <span className="text-slate-300">-</span>}</td>
                          <td className="px-2 py-2 text-right tabular-nums text-slate-600">{p.count}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{formatNumber(p.hrsSaved)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        )}
        {visible.late && lateProjects.length > 0 && (
          <ChartCard icon={AlertTriangle} iconGrad="from-[#EF3340] to-[#EF3340] text-white" title="Late Projects" subtitle="Forecast projects past their due date"
            info={<>
              <strong>Late Projects</strong>
              <p>Projects still in <strong>Forecast</strong> status whose due date has passed. These are not captured by the On Time KPI (which only measures completed projects).</p>
              <p className="text-slate-300">To resolve: update the due date, move to Complete, or cancel.</p>
            </>}
            badge={<Badge cls="bg-[#EF3340]/10 text-[#EF3340]">{lateProjects.length} late</Badge>}>
            <div className="space-y-2">
              {lateProjects
                .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
                .map(p => {
                  const daysLate = Math.floor((Date.now() - new Date(p.dueDate).getTime()) / (24 * 60 * 60 * 1000))
                  return (
                    <Link key={p.id} to={`/projects/${p.id}`}
                      className="flex items-center gap-3 px-3 py-2.5 bg-[#EF3340]/5 rounded-xl border border-[#EF3340]/10 hover:bg-[#EF3340]/10 hover:border-[#EF3340]/25 transition-colors group">
                      <div className="w-1.5 h-8 bg-[#EF3340] rounded-full flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-700 group-hover:text-[#005EB8] truncate">{p.name}</div>
                        <div className="text-xs text-slate-400">{p.subPortfolio} · {p.tier}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-bold text-[#EF3340]">{daysLate}d late</div>
                        <div className="text-[10px] text-slate-400">Due {new Date(p.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      </div>
                    </Link>
                  )
                })}
            </div>
          </ChartCard>
        )}
        {visible.late && lateProjects.length === 0 && (
          <ChartCard icon={AlertTriangle} iconGrad="from-[#00843D] to-[#00843D] text-white" title="Late Projects" subtitle="Forecast projects past their due date"
            badge={<Badge cls="bg-[#00843D]/10 text-[#00843D]">All on track</Badge>}>
            <p className="text-sm text-slate-400 text-center py-6">No forecast projects are past their due date.</p>
          </ChartCard>
        )}
        {visible.risks && (
          <ChartCard icon={ShieldAlert} iconGrad={openRisks.length > 0 ? 'from-[#F88806] to-[#F88806] text-white' : 'from-[#00843D] to-[#00843D] text-white'} title="Open Risks" subtitle="Active risks logged on projects, rated Impact x Likelihood"
            info={<>
              <strong>Open Risks</strong>
              <p>Risks logged on the Project Detail page with status <strong>Open</strong>, across in-flight and delivered projects in the current filter.</p>
              <p>Rated on the Danone matrix: Impact x Likelihood = 1-16, banded <strong>Red</strong> (9+), <strong>Amber</strong> (4-8), <strong>Green</strong> (1-3).</p>
              <p>Open risks rated <strong>{riskBand}</strong> drive the Forecast view: <strong>Delivery</strong> -&gt; On Time, <strong>Scope</strong> -&gt; In Full, <strong>Cost</strong> -&gt; At Cost.</p>
              <p className="text-slate-300">Mitigated and closed risks are not shown here - the project's Risks card keeps the history. Use Export → Risk Register for the full Danone-format register.</p>
            </>}
            badge={openRisks.length > 0
              ? <span className="flex items-center gap-1">
                  {(['red', 'amber', 'green'] as const).map(band => openRagCounts[band] > 0 && (
                    <Badge key={band} cls={RAG_PILL[band]}>{openRagCounts[band]} {RAG_LABEL[band].toLowerCase()}</Badge>
                  ))}
                </span>
              : <Badge cls="bg-[#00843D]/10 text-[#00843D]">None open</Badge>}>
            {openRisks.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No open risks logged on projects in the current filter.</p>
            ) : (
              <div className="space-y-2">
                {openRisks.map(({ project: p, risk: r }) => {
                  const score = riskScore(r)
                  const rag = riskRag(r)
                  return (
                    <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 bg-[#F88806]/5 rounded-xl border border-[#F88806]/10">
                      <div className={`w-1.5 h-8 rounded-full flex-shrink-0 ${rag ? RAG_BAR[rag] : 'bg-slate-300'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{r.title}</div>
                        <div className="text-xs text-slate-400 truncate">
                          {p.name} · {r.category}
                          {r.deadline && <> · mitigation due {new Date(r.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>}
                        </div>
                      </div>
                      {score !== null && rag ? (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${RAG_PILL[rag]}`}>{score} · {RAG_LABEL[rag]}</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 bg-slate-100 text-slate-500" title="Logged before the risk matrix - edit on the project page to assess">Not assessed</span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </ChartCard>
        )}
        {visible.adoption && (
          <ChartCard icon={Users} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="Adoption Rate" subtitle="Actual vs target users by project"
            info={<>
              <strong>Adoption Rate</strong>
              <p>Per-project adoption: actual users divided by expected users, as a percentage.</p>
              <p>Percent-mode projects are back-calculated to a user count so everything lives in one formula. Projects marked <strong>n/a</strong> are excluded.</p>
            </>}
            badge={<Badge cls={kpis.adoption >= 80 ? 'bg-[#00843D]/10 text-[#00843D]' : kpis.adoption >= 60 ? 'bg-[#F88806]/10 text-[#F88806]' : 'bg-[#EF3340]/10 text-[#EF3340]'}>Avg {kpis.adoption}%</Badge>}>
            <ChartContainer config={adoptionConfig} className="aspect-auto h-52 w-full">
              <BarChart data={adoptionData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
                <XAxis dataKey="project" tickLine={false} axisLine={false} tick={AXIS_TICK} interval={0} />
                <YAxis domain={[0, 100]} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={v => `${v}%`} />
                <ChartTooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} content={<ChartTooltipContent formatter={(value, name, item) => (
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-[2px]" style={{ background: item.color }} />
                    <span className="text-muted-foreground">{adoptionConfig[name as string]?.label ?? name}</span>
                    <span className="ml-auto font-mono font-medium tabular-nums text-foreground">{value}%</span>
                  </div>
                )} />} />
                <Bar dataKey="adoption" fill="var(--color-adoption)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </ChartCard>
        )}
        {visible.cost && (
          <ChartCard icon={DollarSign} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="Cost Summary" subtitle="Budget overview across all projects"
            info={<>
              <strong>Cost Summary</strong>
              <p>Total forecast and actual spend across all projects in the filter, plus the variance as a single number.</p>
            </>}
            badge={<Badge cls={costVar >= 0 ? 'bg-[#00843D]/10 text-[#00843D]' : 'bg-[#EF3340]/10 text-[#EF3340]'}>{costVar >= 0 ? `${costVar}% under` : `${Math.abs(costVar)}% over`}</Badge>}>
            <div className="space-y-3">
              {[
                { label: 'Total Forecast', value: formatCurrency(totalForecast), sub: `across ${active.length} projects`, accent: 'bg-[#002677]' },
                { label: 'Total Actual',   value: formatCurrency(totalActual),   sub: costVar >= 0 ? 'Under budget' : 'Over budget',  accent: costVar >= 0 ? 'bg-[#00843D]' : 'bg-[#EF3340]' },
                { label: 'Variance',       value: `${totalForecast >= totalActual ? '+' : '-'}${formatCurrency(Math.abs(totalForecast - totalActual))}`, sub: costVar >= 0 ? `${costVar}% saved` : `${Math.abs(costVar)}% over budget`, accent: costVar >= 0 ? 'bg-[#00843D]' : 'bg-[#EF3340]' },
              ].map(row => (
                <div key={row.label} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <div className={`w-1 h-10 ${row.accent} rounded-full flex-shrink-0`} />
                  <div className="flex-1">
                    <div className="text-xs text-slate-400 font-medium">{row.label}</div>
                    <div className="text-lg font-bold text-slate-800 leading-tight">{row.value}</div>
                  </div>
                  <span className="text-xs text-slate-500 font-medium">{row.sub}</span>
                </div>
              ))}
            </div>
          </ChartCard>
        )}
        {visible.rgm && (portfolioFilter.length === 0 || portfolioFilter.includes('RGM')) && (() => {
        const rgmProjects = filtered.filter(p => p.subPortfolio === 'RGM')
        const totalTradeSpend = rgmProjects.reduce((sum, p) => sum + (p.tradeSpendReduction || 0), 0)
        const totalGrowth = rgmProjects.reduce((sum, p) => sum + (p.growth || 0), 0)
        return (<>
            {[
              { title: 'Trade Spend Reduction', total: totalTradeSpend, field: 'tradeSpendReduction' as const },
              { title: 'Growth',                total: totalGrowth,      field: 'growth' as const },
            ].map(section => (
              <ChartCard key={section.title} icon={TrendingUp} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title={`RGM - ${section.title}`} subtitle="Across RGM projects"
                info={<>
                  <strong>RGM - {section.title}</strong>
                  <p>Per-project {section.title.toLowerCase()} across all projects in the RGM sub-portfolio. Only shown when RGM is in scope.</p>
                </>}>
                <div className="space-y-2">
                  {rgmProjects.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No RGM projects</p>
                  ) : rgmProjects.map(p => (
                    <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="w-1.5 h-8 bg-[#00843D] rounded-full flex-shrink-0" />
                      <div className="text-sm font-medium text-slate-700 truncate flex-1">{p.name}</div>
                      <div className="text-sm font-bold text-[#00843D]">{p[section.field] !== undefined ? formatCurrency(p[section.field]!) : '-'}</div>
                    </div>
                  ))}
                  {rgmProjects.length > 0 && (
                    <div className="flex items-center justify-between px-3 py-2.5 bg-[#00843D]/10 rounded-xl border border-[#00843D]/20 mt-1">
                      <span className="text-sm font-semibold text-[#00843D]">Total</span>
                      <span className="text-sm font-bold text-[#00843D]">{formatCurrency(section.total)}</span>
                    </div>
                  )}
                </div>
              </ChartCard>
            ))}
        </>)
      })()}
      </MasonryColumns>

    </div>
  )
}

// Two-column masonry for the report cards: children are dealt alternately into
// two independent flex columns (evens left, odds right - the same left-to-right
// reading order as the old grid) so every card packs tightly under the one
// above it regardless of its neighbour's height. Fragments (e.g. the Cost pair)
// are flattened so each card is distributed individually; hidden sections
// (`false`/null children) are dropped by Children.toArray.
function MasonryColumns({ children }: { children: ReactNode }) {
  const items = Children.toArray(children).flatMap(item =>
    isValidElement(item) && item.type === Fragment
      ? Children.toArray((item.props as { children?: ReactNode }).children).map(inner =>
          // Children.toArray keys restart at .0 inside each fragment - prefix
          // with the fragment's own key so flattened siblings never collide.
          isValidElement(inner) ? cloneElement(inner, { key: `${item.key}${inner.key}` }) : inner
        )
      : [item]
  )
  return (
    <div className="flex items-start gap-4 mb-4">
      <div className="flex-1 min-w-0 flex flex-col gap-4">{items.filter((_, i) => i % 2 === 0)}</div>
      <div className="flex-1 min-w-0 flex flex-col gap-4">{items.filter((_, i) => i % 2 === 1)}</div>
    </div>
  )
}

// SpiralWordCloud: measures real text widths via canvas so bounding boxes are
// accurate, then places words in an Archimedean spiral with uniform 4px gaps.
function SpiralWordCloud({ words, width, height, colors }: {
  words: { text: string; value: number }[]
  width: number
  height: number
  colors: string[]
}) {
  type WordRect = { text: string; x: number; y: number; fs: number; color: string; rotate: number }
  const [layout, setLayout] = useState<WordRect[]>([])

  useEffect(() => {
    const sorted = [...words].sort((a, b) => b.value - a.value).slice(0, 60)
    if (!sorted.length) { setLayout([]); return }
    const maxVal = sorted[0].value
    const minVal = sorted[sorted.length - 1].value

    const sr = (seed: number) => { const x = Math.sin(seed * 9301 + 49297) * 233280; return x - Math.floor(x) }
    const GAP = 5  // uniform gap between every word

    // Measure real pixel widths using an offscreen canvas
    const cvs = document.createElement('canvas')
    const ctx = cvs.getContext('2d')!
    const measureW = (text: string, fs: number) => {
      ctx.font = `600 ${fs}px Nunito, sans-serif`
      return ctx.measureText(text).width
    }

    const placed: { x1: number; y1: number; x2: number; y2: number }[] = []
    const res: WordRect[] = []

    for (let i = 0; i < sorted.length; i++) {
      const { text, value } = sorted[i]
      const t = maxVal > minVal ? (value - minVal) / (maxVal - minVal) : 0.5
      const fs = Math.round(t * 46) + 14
      // Top words horizontal; ~30% of the rest vertical
      const rotate = (t > 0.55 || sr(i * 7 + 2) > 0.3) ? 0 : 90

      const tw = measureW(text, fs)
      const th = fs * 1.15
      const bw = (rotate === 90 ? th : tw) + GAP * 2
      const bh = (rotate === 90 ? tw : th) + GAP * 2

      let fx = -1, fy = -1
      for (let step = 0; step < 4000; step++) {
        const angle = step * 0.3
        const r = step * 0.9
        const cx = width / 2 + Math.cos(angle) * r
        const cy = height / 2 + Math.sin(angle) * r * 0.55
        const x1 = cx - bw / 2, y1 = cy - bh / 2
        const x2 = cx + bw / 2, y2 = cy + bh / 2
        if (x1 < 2 || y1 < 2 || x2 > width - 2 || y2 > height - 2) continue
        if (placed.every(p => x2 <= p.x1 || x1 >= p.x2 || y2 <= p.y1 || y1 >= p.y2)) {
          placed.push({ x1, y1, x2, y2 }); fx = cx; fy = cy; break
        }
      }
      if (fx >= 0) res.push({ text, x: fx, y: fy, fs, color: colors[i % colors.length], rotate })
    }
    setLayout(res)
  }, [words, width, height])

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {layout.map(({ text, x, y, fs, color, rotate }) => (
        <text
          key={text}
          x={x} y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fs}
          fill={color}
          fontFamily="Nunito, sans-serif"
          fontWeight={600}
          transform={rotate !== 0 ? `rotate(${rotate},${x},${y})` : undefined}
        >
          {text}
        </text>
      ))}
    </svg>
  )
}

// SlideFrame renders a fixed-size 16:9 canvas (1280×720 by default) and scales
// it to fit the viewport so the user can screenshot at exact PowerPoint aspect
// ratio. Esc or the × button exits the slide view.
function SlideFrame({ width, height, onExit, extraControls, children }: {
  width: number
  height: number
  onExit: () => void
  extraControls?: ReactNode
  children: ReactNode
}) {
  const [scale, setScale] = useState(1)

  useEffect(() => {
    // Scale the 1280×720 canvas to fit the available viewport (minus padding).
    const fit = () => {
      const padX = 80
      const padY = 120
      const availW = window.innerWidth - padX
      const availH = window.innerHeight - padY
      setScale(Math.min(availW / width, availH / height))
    }
    fit()
    window.addEventListener('resize', fit)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onExit() }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('resize', fit)
      window.removeEventListener('keydown', onKey)
    }
  }, [width, height, onExit])

  return (
    <div className="fixed inset-0 bg-slate-800 z-40 flex flex-col items-center justify-center overflow-auto">
      {/* Top bar - outside the screenshot frame */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
        {extraControls}
        <button
          onClick={onExit}
          className="px-3 py-1.5 rounded-lg bg-white text-slate-700 text-xs font-semibold hover:bg-slate-100 flex items-center gap-2"
        >
          Exit (Esc)
        </button>
      </div>
      {/* The 16:9 canvas */}
      <div
        style={{
          width,
          height,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
        className="bg-white shadow-2xl rounded-lg overflow-hidden"
      >
        {children}
      </div>
    </div>
  )
}
